import {clone, find, findIndex, isArray, isInteger, isObject, isString, orderBy, union} from 'lodash';
import contentService from '../../services/contentService';
import userContactService from '../../services/userContactService';
import presentationService from '../../../../ml/services/presentationService';
import snippetService from "../../services/snippetService";
import shareTemplateService from '../../../../ml/services/shareTemplateService';
import fetchService from '../../services/fetchService';
import moment from 'moment-timezone';
import localForage from 'localforage';

const yesNoOptions = [ // MLD-3565
  {
    id: '',
    name: 'Yes or No',
  },
  {
    id: 'yes',
    name: 'Yes',
  },
  {
    id: 'no',
    name: 'No',
  },
];

const scopes = [ // MLD-3565
  {
    id: 'folder',
    name: 'Current folder',
  },
  {
    id: 'everywhere',
    name: 'Everywhere',
  },
];

const sortOptions = [
  {
    id: 1,
    name: 'Name (A-Z)',
    fields: ['is_new', 'is_featured', 'slug'],
    orders: ['desc', 'desc', 'asc'],
    icon: 'fal fa-sort-alpha-down',
  },
  {
    id: 2,
    name: 'Newest to oldest',
    fields: ['is_new', 'is_featured', 'content_updated_at', 'slug'],
    orders: ['desc', 'desc', 'desc', 'asc'],
    icon: 'fal fa-sort-amount-up',
  },
];

const worldFolder = { // MLD-7739
  id: -6,
  name: 'All Files',
  is_world: true,
  is_home: false,
  is_bookmarks: false,
  is_new: false, // MLD-5639
  is_private: false,
  is_slides: false,
  children: [],
  veeva_disabled: false,
  icon: 'fas fa-fw fa-globe-americas',
};

const homeFolder = {
  id: -1,
  name: 'Home',
  is_world: false,
  is_home: true,
  is_bookmarks: false,
  is_new: false, // MLD-5639
  is_private: false,
  is_slides: false,
  children: [],
  veeva_disabled: false,
  icon: 'fas fa-fw fa-home',
};

const bookmarksFolder = { // MLD-2107
  id: -2,
  name: 'My Favorites',
  is_world: false,
  is_home: false,
  is_bookmarks: true,
  is_new: false, // MLD-5639
  is_private: false,
  is_slides: false,
  children: [],
  veeva_disabled: true,
  icon: 'fas fa-fw fa-bookmark',
};

const slidesFolder = { // MLD-6435
  id: -3,
  name: 'Slide Decks',
  is_world: false,
  is_home: false,
  is_bookmarks: false,
  is_new: false,
  is_private: false,
  is_slides: true,
  children: [],
  veeva_disabled: true,
  icon: 'fab fa-fw fa-slideshare',
}

const myFolder = { // MLD-2729
  id: -4,
  name: 'My Content',
  is_world: false,
  is_home: false,
  is_bookmarks: false,
  is_new: false, // MLD-5639
  is_private: true,
  is_slides: false,
  children: [],
  veeva_disabled: true,
  icon: 'fas fa-fw fa-user-lock',
}

const newFolder = { // MLD-5639
  id: -5,
  name: 'New Content',
  is_world: false,
  is_home: false,
  is_bookmarks: false,
  is_new: true,
  is_private: false,
  is_slides: false,
  children: [],
  veeva_disabled: true,
  icon: 'fas fa-fw fa-bells',
}

function initForm(form = null) {
  return {
    scope: form && form.scope ? form.scope : 'folder',
    brand: form && form.brand ? form.brand : '',
    product: form && form.product ? form.product : '',
    label: form && form.label ? form.label : '',
    name: form && form.name ? form.name : '',
    can_be_shared: form && form.can_be_shared ? form.can_be_shared : '',
    sortOption: form && form.sortOption ? form.sortOption : sortOptions[0],
    homeSortOption: form && form.homeSortOption ? form.homeSortOption : sortOptions[0],
    type: form && form.type ? form.type : '',
  };
}

const state = {
  activity: null,
  activeFolders: [homeFolder.id],
  bookmarkBusy: false, // MLD-5591
  bookmarks: [], // MLD-2107
  bookmarksFolder: bookmarksFolder,
  brands: [], // MLD-4796
  folder: homeFolder,
  loadingFolders: false, // MLD-7933
  folders: [],
  folderDrawer: true,
  homeFolder: homeFolder,
  worldFolder: worldFolder, // MLD-7739
  liveMode: true,
  mustExitProperly: false, // MLD-2940
  myFolder: myFolder, // MLD-2729
  newFolder: newFolder, // MLD-5639
  slidesFolder: slidesFolder, // MLD-6435
  openFolders: [], // MLD-3090
  presentationsLoading: false, // MLD-2729
  presentation: null,
  filteredPresentations: [], // MLD-7739
  presentations: [],
  products: [], // MLD-3306
  session: null,
  snippets: [], // MLD-3900
  sortOption: sortOptions[0],
  homeSortOption: sortOptions[0], // MLD-5560
  sortOptions: sortOptions,
  spaces: [], // MLD-4908
  templates: [], // MLD-5082
  theme: null,
  themes: [], // MLD-2403
  userContacts: [], // MLD-2588
  form: initForm(),
  // MLD-5823
  customerPickedAt: null,
  customerPickerDialog: false,
  bypassCustomerSelection: false,
  selectedCustomers: [],
  customersSelectedAt: null,
  isFullScreen: false, // MLD-6890
}

const mutations = {
  setActivity(state, activity) {
    state.activity = activity;
  },
  setActiveFolders(state, activeFolders) {
    state.activeFolders = activeFolders;
  },
  setBookmarkBusy(state, bookmarkBusy) { // MLD-5591
    state.bookmarkBusy = bookmarkBusy;
  },
  setBookmark(state, bookmark) { // MLD-2107
    if (isObject(bookmark) && bookmark.id) {
      const index = findIndex(state.bookmarks, b => b.id === bookmark.id);
      if (index > -1) {
        state.bookmarks[index] = bookmark;
      } else {
        state.bookmarks.push(bookmark);
      }
    }
  },
  removeBookmark(state, presentationID) { // MLD-2107
    state.bookmarks = state.bookmarks.filter(b => b.presentation_id !== presentationID);
  },
  setBookmarks(state, bookmarks) { // MLD-2107
    state.bookmarks = bookmarks;
  },
  setBrands(state, brands) { // MLD-4796
    state.brands = brands;
  },
  setFilteredPresentations(state, filteredPresentations) { // MLD-7739
    state.filteredPresentations = filteredPresentations;
  },
  setFolderDrawer(state, folderDrawer) {
    state.folderDrawer = folderDrawer;
  },
  setFolder(state, folder) {
    if (isObject(folder) && folder.id) {
      state.folder = folder;
      state.activeFolders = [folder.id];
      const form = initForm(state.form);
      form.scope = 'folder'
      state.form = form;
    } else {
      state.folder = homeFolder;
      state.activeFolders = [homeFolder.id];
      const form = initForm(state.form);
      form.scope = 'everywhere';
      state.form = form;
    }
  },
  setLoadingFolders(state, loadingFolders) { // MLD-7933
    state.loadingFolders = loadingFolders;
  },
  setFolders(state, folders) {
    state.folders = folders;
  },
  setLiveMode(state, liveMode) {
    state.liveMode = liveMode;
  },
  setMustExitProperly(state, mustExitProperly) { // MLD-2940
    state.mustExitProperly = mustExitProperly;
  },
  setOpenFolders(state, openFolders) { // MLD-3090
    if (isInteger(openFolders)) {
      state.openFolders = [openFolders];
    } else if (isArray(openFolders)) {
      state.openFolders = openFolders;
    } else {
      state.openFolders = [];
    }
  },
  setPresentation(state, presentation) {
    state.presentation = presentation;
  },
  setPresentationsLoading(state, presentationsLoading) { // MLD-2729
    state.presentationsLoading = presentationsLoading;
  },
  setPresentations(state, presentations) {
    state.presentations = presentations;
  },
  setProducts(state, products) { // MLD-3306
    state.products = products;
  },
  setSnippets(state, snippets) { // MLD-3900
    state.snippets = snippets;
  },
  setSession(state, session) {
    state.session = session;
    if (session) { // MLD-2811
      state.liveMode = session.live_mode;
    }
  },
  setSpaces(state, spaces) { // MLD-4908
    state.spaces = spaces;
  },
  setTheme(state, theme) {
    state.theme = theme;
  },
  setThemes(state, themes) { // MLD-2403
    state.themes = themes;
    if (themes.length) {
      state.theme = find(this.themes, t => t.use_in_content_library);
    } else {
      state.theme = null;
    }
  },
  setUserContacts(state, userContacts) { // MLD-2588
    state.userContacts = userContacts;
  },
  setFormValue(state, obj) { // MLD-3565
    const form = initForm(state.form);
    form[obj.key] = obj.value;
    state.form = form;
  },
  setHomeSortOption(state, homeSortOption) { // MLD-5560
    const form = initForm(state.form);
    state.homeSortOption = homeSortOption;
    state.form = form;
    form.homeSortOption = homeSortOption;
  },
  setSortOption(state, sortOption) { // MLD-3565
    const form = initForm(state.form);
    form.sortOption = sortOption;
    state.form = form;
    state.sortOption = sortOption;
  },
  setTemplates(state, templates) { // MLD-5082
    state.templates = templates;
  },
  resetForm(state, team) { // MLD-3565
    state.sortOption = sortOptions[0];
    // MLD-5560
    if (team?.is_home_folder_sorted_by_date) {
      state.homeSortOption = sortOptions[1];
      initForm().homeSortOption = sortOptions[1];
    } else {
      state.homeSortOption = sortOptions[0];
      initForm().homeSortOption = sortOptions[0];
    }
    state.form = initForm();
    state.filteredPresentations = []; // MLD-7739
  },
  setCustomerPickerDialog(state, customerPickerDialog) { // MLD-5823
    state.customerPickerDialog = customerPickerDialog;
  },
  setSelectedCustomers(state, selectedCustomers) { // MLD-5823
    if (isArray(selectedCustomers) && selectedCustomers.length > 0) {
      state.customersSelectedAt = moment();
    } else {
      state.customersSelectedAt = null;
    }
    state.selectedCustomers = selectedCustomers;
    localForage.setItem('selectedCustomers', selectedCustomers).then();
  },
  setCustomersSelectedAt(state, timestamp) {
    state.customersSelectedAt = timestamp;
    if (timestamp) {
      localForage.setItem('customersSelectedAt', timestamp).then();
    } else {
      localForage.removeItem('customersSelectedAt').then();
    }
  },
  setBypassCustomerSelection(state, bypassCustomerSelection) { // MLD-5823
    state.bypassCustomerSelection = bypassCustomerSelection;
  },
  setIsFullScreen(state, isFullScreen) { // MLD-6890
    state.isFullScreen = isFullScreen;
  }
}

const actions = {
  searchPresentations({commit, getters, rootState, rootGetters}) { // MLD-7739
    let results = clone(getters.presentations);
    const form = getters.form;
    const sortOption = form.sortOption

    if (form.name?.trim()) { // MLD-3306
      const name = form.name.trim().toLowerCase();
      const nameTerms = name.split(',').map(n => n.trim());
      results = results.filter(p => nameTerms.some(n => p.name.toLowerCase().includes(n)));
    }

    if (getters.isWorld) { // MLD-7739
      // Searching all files, so no additional filtering is required.
    } else if (getters.isHome) { // MLD-6001 Exclude private presentations from the Home folder.
      results = results.filter(p => p.show_in_main_list && p.is_private === false);
    } else if (getters.isNewFolder) { // MLD-5639
      results = results.filter(p => p.is_new);
    } else if (getters.isBookmarks) {
      const bookmarkedPresentations = getters.bookmarkedPresentations;
      results = results.filter(p => bookmarkedPresentations.includes(p.id));
    } else if (getters.isMyFolder) { // MLD-2729
      results = results.filter(p => p.owner_id === rootGetters.user?.id);
    } else if (getters.isSlidesFolder) { // MLD-6435
      results = results.filter(p => p.is_slide_deck);
    } else if (getters.folder) {
      results = results.filter(p => getters.folder.presentation_ids?.includes(p.id));
    }

    if (isObject(form.product) && isArray(form.product.presentation_ids)) { // MLD-3306
      results = results.filter(p => form.product.presentation_ids.includes(p.id));
    }

    if (isInteger(form.brand)) { // MLD-4796
      results = results.filter(p => p.brand_id === parseInt(form.brand));
    }

    if (isString(form.type) && form.type) {
      results = results.filter(p => p.presentation_type === form.type);
    }

    if (form.can_be_shared && ['yes', 'no'].includes(form.can_be_shared)) {
      results = results.filter((p) =>
        form.can_be_shared === 'yes' ? p.can_be_shared : !p.can_be_shared
      );
    }

    if (isInteger(form.label)) {
      results = results.filter(p => {
        const labelIDs = p.labels.map(l => l.id);
        return labelIDs.includes(form.label);
      });
    }

    const sortedResults = orderBy(results, sortOption.fields, sortOption.orders);
    commit('setFilteredPresentations', sortedResults);
  },
  initializeHomeSortOption({commit, dispatch, getters}, team) { // MLD-5560
    if (team?.is_home_folder_sorted_by_date) {
      dispatch('setHomeSortOption', getters.sortOptions[1]);
    } else if (team?.is_home_folder_sorted_by_name) {
      dispatch('setHomeSortOption', getters.sortOptions[0]);
    }
  },
  resetForm({commit}, team) { // MLD-5560
    commit('resetForm', team);
  },
  setHomeSortOption({commit, dispatch}, homeSortOption) { // MLD-5560
    commit('setHomeSortOption', homeSortOption);
  },
  setFormValue({commit}, object) { // MLD-5644
    commit('setFormValue', object);
  },
  setSortOption({commit, dispatch}, sortOption) {
    commit('setSortOption', sortOption);
  },
  setFormScope({commit, dispatch}, scope) { // MLD-3565
    console.log('setFormScope', scope);
    commit('setFormValue', {key: 'scope', value: scope});
    if (scope === 'everywhere') {
      dispatch('updateActiveFolders', worldFolder.id);
    }
    /*if (scope === 'everywhere') {
      dispatch('updateActiveFolders', null);
    }*/
  },
  async fetchUserData({commit, dispatch}) { // MLD-3342
    dispatch('fetchBookmarks').then().catch(err => console.log('fetchBookmarks exception', err)); // MLD-2107
    dispatch('fetchBrands').then().catch(err => console.log('fetchBrands exception', err)); // MLD-2107
    dispatch('fetchFolders').then().catch(err => console.log('fetchFolders exception', err));
    dispatch('fetchProducts').then().catch(err => console.log('fetchProducts exception', err)); // MLD-3306
    dispatch('fetchPresentations')
      .then(() => {
        // MLD-7739 We have to search presentations after fetching them to populate the Content Library correctly.
        //console.log('fetchPresentations success. Searching presentations.');
        dispatch('searchPresentations');
      })
      .catch(err => console.log('fetchPresentations exception', err));
  },
  async toggleBookmark({commit, dispatch, getters}, presentationID) { // MLD-5591
    if (getters.bookmarkBusy) {
      return;
    }
    commit('setBookmarkBusy', true);
    return axios.post(`/api/bookmarks/presentations/${presentationID}/toggle`)
      .then(response => {
        const bookmark = response.data;
        if (isObject(bookmark) && bookmark.id) {
          commit('setBookmark', bookmark);
        } else {
          commit('removeBookmark', presentationID);
        }
        return true;
      })
      .finally(() => {
        commit('setBookmarkBusy', false);
      })
  },
  async fetchBookmarks({commit}) { // MLD-2107
    const {data} = await axios.get(`/api/bookmarks`);
    commit('setBookmarks', data);
  },
  async fetchBrands({commit}) { // MLD-4796
    const {data} = await axios.get(`/api/brands`)
    commit('setBrands', data);
  },
  async fetchFolders({commit}, params = {}) {
    try {
      commit('setLoadingFolders', true)
      const options = {
        params,
      };
      const {data} = await axios.get(`/api/user/folders`, options)
      commit('setFolders', data);
    } catch (error) {
      commit('setFolders', [])
    } finally {
      commit('setLoadingFolders', false)
    }
  },
  async fetchPresentation({commit}, presentationID) {
    return axios.get(`/api/presentations/${presentationID}?include_files=false`)
      .then(response => {
        const presentation = response.data;
        if (presentation.is_active) {
          commit('setPresentation', response.data);
          return response.data;
        }
        throw new Error(`Presentation ${presentationID} is not active.`);
      })
      .catch(err => {
        // MLD-5483
        commit('setPresentation', null);
        throw err;
      });
  },
  // MLD-4860
  async fetchPresentationsInChunks({commit}) {
    console.log('fetchPresentationsInChunks');
    const url = `/api/user/presentations`;
    let chunkSize = 200;
    let concurrentRequests = 3;
    const form = { // MLD-6951
      include_files: false,
      operator: '>=', // MLD-8122
    };
    let presentations = [];
    try {
      presentations = await fetchService.fetchRecords(url, form, chunkSize, concurrentRequests);
      commit('setPresentations', presentations);
    } catch (e) {
      console.log('fetchPresentationsInChunks exception:', e.message);
      commit('setPresentations', []);
    }
  },
  // MLD-4860 - this is the slower implementation because it fetches all presentations at once
  async fetchAllPresentationsAtOnce({commit}) {
    try {
      const {data: presentations} = await axios.get(`/api/user/presentations`);
      commit('setPresentations', presentations);
    } catch (err) {
      commit('setPresentations', []); // MLD-2729
    }
    return Promise.resolve();
  },
  async fetchPresentations({commit, dispatch}) {
    console.time('fetchPresentations');
    commit('setPresentationsLoading', true);
    await dispatch('fetchPresentationsInChunks')
    commit('setPresentationsLoading', false);
    console.timeEnd('fetchPresentations');
    return Promise.resolve();
  },
  activatePresentation({commit, dispatch, getters}, presentationID) {
    const presentation = find(getters.presentations, p => p.id === presentationID);
    commit('setPresentation', presentation);
    return Promise.resolve(true);
  },
  setActivity({commit}, activity) {
    commit('setActivity', activity);
  },
  updateOpenFolders({commit, dispatch}, openFolders) { // MLD-3090
    commit('setOpenFolders', openFolders);
  },
  updateActiveFolders({commit, dispatch}, activeFolders) {
    if (isInteger(activeFolders)) {
      dispatch('setActiveFolderID', activeFolders);
    } else if (isArray(activeFolders) && activeFolders.length) {
      dispatch('setActiveFolderID', activeFolders[0]);
    } else {
      dispatch('setActiveFolderID', homeFolder.id);
    }
  },
  setActiveFolderID({commit, dispatch, getters}, folderID) {
    if (isInteger(folderID)) {
      if (folderID === worldFolder.id) { // MLD-7739
        commit('setFolder', worldFolder);
      } else if (folderID === bookmarksFolder.id) {
        commit('setFolder', bookmarksFolder);
      } else if (folderID === homeFolder.id) {
        commit('setFolder', homeFolder);
      } else if (folderID === myFolder.id) { // MLD-2729
        commit('setFolder', myFolder);
      } else if (folderID === newFolder.id) { // MLD-5639
        commit('setFolder', newFolder);
      } else if (folderID === slidesFolder.id) { // MLD-6435
        commit('setFolder', slidesFolder);
      } else {
        const folder = find(getters.folders, f => f.id === folderID);
        commit('setFolder', folder);
      }
    } else {
      commit('setFolder', homeFolder);
    }
    return Promise.resolve(true);
  },
  setBrand({commit}, brand) { // MLD-4796
    commit('setFormValue', {key: 'brand', value: brand}); // MLD-3565
  },
  setFolder({commit}, folder) {
    commit('setFolder', folder);
  },
  setFolders({commit}, folders) {
    commit('setFolders', folders);
  },
  toggleFolderDrawer({commit, getters}) {
    commit('setFolderDrawer', !getters.folderDrawer);
  },
  setFolderDrawer({commit}, folderDrawer) {
    commit('setFolderDrawer', folderDrawer);
  },
  setLiveMode({commit, getters}, liveMode) {
    commit('setLiveMode', liveMode);
    if (getters.session) {
      contentService.toggleLiveMode(getters.session, liveMode)
        .then(data => {
          commit('setSession', data);
        })
        .catch(() => {
        }) // MLD-4585 silently catch this
    }
  },
  setPresentation({commit}, presentation) {
    commit('setPresentation', presentation);
    return Promise.resolve();
  },
  setSession({commit}, session) {
    commit('setSession', session);
    return Promise.resolve();
  },
  fetchSession({commit}, sessionUUID) {
    return contentService.getDeviceSession(sessionUUID)
      .then(data => {
        commit('setSession', data);
      })
      .catch(err => {
        commit('setSession', null);
        throw err;
      })
  },
  fetchSpaces({commit}) { // MLD-4908
    return axios.get(`/api/user/spaces`)
      .then(response => {
        commit('setSpaces', response.data);
      })
  },
  fetchTemplates({commit}) { // MLD-5082
    shareTemplateService.getQuickShareTemplates()
      .then(data => {
        commit('setTemplates', data);
      })
  },
  setProduct({commit}, product) { // MLD-3306
    commit('setFormValue', {key: 'product', value: product}); // MLD-3565
  },
  async fetchProducts({commit}) { // MLD-3306
    const options = {
      params: {
        include: 'presentation_ids',
        status: 'Active',
      }
    }
    const {data} = await axios.get(`/api/products`, options);
    commit('setProducts', orderBy(data, ['slug'], ['asc']));
  },
  async endSession({dispatch}, payload) { // MLD-2463
    if (payload) {
      try {
        await contentService.closePresentation(payload);
      } catch (e) {
      }
      /*try {
        await contentService.endSession(session);
      } catch (e) {
      }*/
      if (payload.activity_id) { // MLD-2463
        try {
          await contentService.endActivity(payload.activity_id);
        } catch (e) {
        }
      }
    }
    await dispatch('setSession', null);
    await dispatch('setPresentation', null);
    await dispatch('setMustExitProperly', false); // MLD-2940
  },
  async setMustExitProperly({commit}, mustExitProperly) { // MLD-2940
    commit('setMustExitProperly', mustExitProperly);
  },
  async fetchThemes({commit}, teamID) { // MLD-2403
    const {data} = await axios.get(`/api/teams/${teamID}/themes`);
    commit('setThemes', data);
  },
  async fetchUserContacts({commit}, form = {}) { // MLD-2588
    const contacts = await userContactService.getContacts(form)
    commit('setUserContacts', contacts);
  },
  async fetchSnippets({commit}, form = {}) { // MLD-3900
    const snippets = await snippetService.getSnippets(form);
    commit('setSnippets', orderBy(snippets, ['slug', 'created_at'], ['asc', 'asc']));
  },
  // MLD-5823
  setSelectedCustomers({commit}, selectedCustomers) {
    commit('setSelectedCustomers', selectedCustomers);
    if (selectedCustomers.length > 0) {
      commit('setCustomersSelectedAt', moment().format());
    } else {
      commit('setCustomersSelectedAt', null);
    }
  },
  async initializeCustomerPicker({commit, dispatch}) {
    const selectedCustomers = await localForage.getItem('selectedCustomers');
    if (isArray(selectedCustomers)) {
      dispatch('setSelectedCustomers', selectedCustomers);
    } else {
      console.log('There are no customers in local storage.');
    }
  },
  setBypassCustomerSelection({commit}, bypassCustomerSelection) {
    commit('setBypassCustomerSelection', bypassCustomerSelection);
  },
  setCustomerPickerDialog({commit}, customerPickerDialog) {
    commit('setCustomerPickerDialog', customerPickerDialog);
  },
  openCustomerPickerDialog({commit}) {
    commit('setCustomerPickerDialog', true);
  },
  closeCustomerPickerDialog({commit}) {
    commit('setCustomerPickerDialog', false);
  },
  setIsFullScreen({commit}, isFullScreen) { // MLD-6890
    commit('setIsFullScreen', isFullScreen);
  }
}

const getters = {
  activity: state => state.activity,
  activityID: state => state.activity ? state.activity.id : null,
  activeFolders: state => state.activeFolders,
  activeProducts(state, getters) {
    return state.products.filter(product => {
      if (product.is_active && union(product.presentation_ids, getters.presentationIDs).length) {
        return true;
      }
      return false;
    })
  },
  brands: state => state.brands, // MLD-4796
  activeBrands(state, getters) { // MLD-4796
    return state.brands.filter(brand => {
      return !!(brand.is_active && union(brand.presentation_ids, getters.presentationIDs).length);
    });
  },
  bookmarkBusy: state => state.bookmarkBusy, // MLD-5591
  bookmarks: state => state.bookmarks, // MLD-2107
  bookmarkedPresentations: state => { // MLD-2107
    return state.bookmarks.map(b => b.presentation_id)
  },
  bookmarksFolder: state => state.bookmarksFolder,
  isBookmarks: state => { // MLD-2107
    return state.folder && !!state.folder.is_bookmarks;
  },
  isPresentationBookmarked(state, getters) { // MLD-5591
    const presentation = getters.presentation;
    return presentation
      && getters.bookmarkedPresentations.includes(presentation.id);
  },
  brand: state => state.brand, // MLD-4796
  faxablePresentations(state, getters, c) { // MLD-3298
    if (rootGetters.team && rootGetters.team.fax_enabled) {
      return getters.presentations.filter(p => {
        return p.can_be_faxed;
      })
    }
    return [];
  },
  filteredPresentations: state => state.filteredPresentations, // MLD-7739
  folder: state => state.folder,
  folderID: state => state.folder ? state.folder : null,
  loadingFolders: state => state.loadingFolders, // MLD-7933
  folders: state => state.folders,
  folderDrawer: state => state.folderDrawer,
  form: state => state.form,
  //formScope: state => state.form.scope,
  //formBrand: state => state.form.brand,
  //formType: state => state.form.type,
  //formLabel: state => state.form.label,
  //formCanBeShared: state => state.form.can_be_shared,
  //formHomeSortOption: state => state.form.homeSortOption,
  //formSortOption: state => state.form.sortOption,
  //formName: state => state.form.name,
  homeSortOption: state => state.homeSortOption,
  homeFolder: state => state.homeFolder,
  isHome: state => {
    if (!state.folder) {
      return true;
    }
    return state.folder && !!state.folder.is_home;
  },
  isWorld: state => { // MLD-7739
    return state.folder && !!state.folder.is_world;
  },
  liveMode: state => state.liveMode,
  isMyFolder: state => { // MLD-2729
    return state.folder && !!state.folder.is_private;
  },
  myFolder: state => state.myFolder, // MLD-2729
  isNewFolder: state => { // MLD-5639
    return state.folder && state.folder.is_new === true;
  },
  newFolder: state => state.newFolder, // MLD-5639
  hasNewPresentations(state, getters) { // MLD-5639
    return getters.newPresentations.length > 0;
  },
  newPresentations(state, getters) { // MLD-5639
    return getters.presentations.filter(p => p.is_new);
  },
  mustExitProperly: state => state.mustExitProperly, // MLD-2940
  openFolders: state => state.openFolders, // MLD-3090
  myContent(state, getters, rootState, rootGetters) { // MLD-6435
    return getters.presentations.filter(p => p.owner_id === rootGetters.user?.id);
  },
  rootFolders(state, getters, rootState, rootGetters) {
    const folders = [
      homeFolder,
      worldFolder, // MLD-7739
    ];

    // MLD-5639 - if there are "NEW" presentations, show the 'New" folder.
    if (getters.hasNewPresentations) {
      folders.push(state.newFolder);
    }

    const team = rootGetters.team;
    const user = rootGetters.user;
    if (team && user && user.is_full_user) {

      // MLD-3639 I moved bookmarks and my content above the folder tree
      if (team.bookmarks_enabled && getters.bookmarks.length > 0) { // MLD-2107
        folders.push(state.bookmarksFolder);
      }

      /*if (getters.slideDecks.length > 0) { // MLD-6435
        folders.push(state.slidesFolder);
      }*/

      // MLD-2729 - if the user owns presentations, show the "My Content" folder.
      if (getters.myContent.length > 0) {
        folders.push(state.myFolder);
      }
    }

    state.folders.filter(f => f.is_root).forEach(f => {
      folders.push(f);
    })

    return folders;
  },
  presentationsLoading: state => state.presentationsLoading, // MLD-2729
  presentation: state => state.presentation,
  presentationID: state => state.presentation ? state.presentation.id : null,
  presentationIDs: state => state.presentations.map(p => p.id),
  presentations: state => state.presentations,
  presentationTypes: state => presentationService.getTypes('Any'), // MLD-3565
  product: state => state.product, // MLD-3306
  products: state => state.products, // MLD-3306
  scopes: state => scopes, // MLD-3565
  session: state => state.session,
  sessionUUID: state => state.session ? state.session.uuid : null,
  slideDecks(state, getters) { // MLD-6435
    return getters.presentations.filter(p => p.is_slide_deck);
  },
  slidesFolder: state => state.slidesFolder, // MLD-6435
  isSlidesFolder(state) { // MLD-6435
    return state.folder && state.folder.id === slidesFolder.id;
  },
  snippets: state => state.snippets, // MLD-3900
  activeSnippets: state => state.snippets.filter(s => s.is_active), // MLD-3900
  sortOption: state => state.sortOption,
  sortOptions: state => state.sortOptions,
  SORT_OPTION_NAME: state => state.sortOptions[0], // MLD-2729
  SORT_OPTION_DATE: state => state.sortOptions[1], // MLD-2729
  spaces: state => state.spaces, // MLD-4908
  templates: state => state.templates, // MLD-5082
  theme: state => { // MLD-2403
    const themes = state.themes.filter(theme => theme.use_in_content_library);
    if (themes.length) {
      return themes[0];
    }
    return null;
  },
  userContacts: state => state.userContacts,
  yesNoOptions: state => yesNoOptions,
  // MLD-5823
  mustSelectCustomer(state, getters, rootState, rootGetters) {
    if (getters.showCustomerButton === false) {
      return false;
    }
    if (rootGetters.team.prompt_for_customer_before_session === false) {
      return false;
    }
    if (getters.liveMode === false) {
      return false;
    }
    if (getters.bypassCustomerSelection) {
      return false;
    }
    if (getters.customersSelectedAt) {
      const cutoff = moment().subtract(rootGetters.team.prompt_for_customer_timeout, 'minutes');
      if (cutoff.isBefore(getters.customersSelectedAt)) {
        return false;
      }
    }
    return getters.selectedCustomers.length === 0;
  },
  customerPickerDialog: state => state.customerPickerDialog,
  bypassCustomerSelection: state => state.bypassCustomerSelection,
  selectedCustomers: state => state.selectedCustomers,
  storedCustomers: state => state.selectedCustomers, // MLD-6667
  customersSelectedAt(state) {
    if (state.customersSelectedAt) {
      return moment(state.customersSelectedAt);
    }
    return null;
  },
  showCustomerButton(state, getters, rootState, rootGetters) {
    return rootGetters.team
      && rootGetters.team.uses_salesforce
      && rootGetters.team.show_customer_button_in_webapp;
  },
  isFullScreen: state => state.isFullScreen, // MLD-6890
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
